import { createFileRoute } from '@tanstack/react-router';
import { Group, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { FileLock2, ScrollText, ShieldCheck } from 'lucide-react';
import { Badge, Card, EmptyState, PageContainer, PageHeader, Section, Table, type TableColumn } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { cardItem } from '../lib/motion';
import { useAuditTrail, type AuditRow } from '../hooks/useAdmin';
import { colors } from '../theme/tokens';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAudit,
});

function AdminAudit() {
  const { data, isPending, isError, error, refetch } = useAuditTrail();

  const columns: ReadonlyArray<TableColumn<AuditRow>> = [
    { key: 'ts', header: 'When', cell: (a) => <Text size="xs" c="dimmed" ff="JetBrains Mono">{a.ts}</Text> },
    { key: 'actor', header: 'Actor', cell: (a) => <Text size="sm" ff="JetBrains Mono">{a.actor}</Text> },
    {
      key: 'tenant',
      header: 'Tenant',
      cell: (a) => <Text size="xs" c={colors.hudCyan} ff="JetBrains Mono">{a.tenantId}</Text>,
    },
    { key: 'action', header: 'Action', cell: (a) => <Text size="sm" ff="JetBrains Mono" c="rgb(var(--c-txt-bright))">{a.action}</Text> },
    { key: 'resource', header: 'Resource', cell: (a) => <Text size="xs" c="dimmed" ff="JetBrains Mono">{a.resource}</Text> },
    {
      key: 'outcome',
      header: 'Outcome',
      cell: (a) => <Badge variant={a.outcome === 'allow' ? 'success' : 'danger'} size="xs">{a.outcome}</Badge>,
    },
    {
      key: 'cor',
      header: 'Correlation',
      cell: (a) => <Text size="xs" c="dimmed" ff="JetBrains Mono">{a.correlationId}</Text>,
    },
  ];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={ScrollText}
        title="Audit Log"
        description="Immutable record of privileged actions across the platform."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Audit' }]}
        actions={
          <Group gap="xs">
            <FileLock2 size={14} color={colors.hudCyan} />
            <Text size="xs" c="dimmed">
              Object-Lock retained · 7 years
            </Text>
          </Group>
        }
      />

      <Section
        title="Last 24 hours"
        description="Every authorization decision, deploy, objective verification, and admin action lands in the per-tenant S3 Object-Lock bucket. The view here is a 24h projection — full history is exportable from the bucket via SOC 2 evidence flow."
      >
        {isError ? (
          <ErrorState
            title="Could not load audit trail"
            message={(error as Error)?.message ?? 'Unknown error'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <LoadingState rows={5} height={28} />
        ) : data && data.length === 0 ? (
          <EmptyState
            illustration={<ShieldCheck size={48} />}
            title="No events in the last 24h"
            description="Audit entries are written to the Object-Lock bucket the moment any privileged action runs. Quiet periods are expected outside training hours."
          />
        ) : (
          <motion.div variants={cardItem} initial="hidden" animate="show">
            <Card>
              <Stack gap="md">
                <Group gap="xs">
                  <ShieldCheck size={16} color={colors.neon} />
                  <Text fw={600} size="sm">
                    Last 24 hours ({data?.length ?? 0} events)
                  </Text>
                </Group>
                <Table columns={columns} rows={data ?? []} rowKey={(a) => a.correlationId} />
              </Stack>
            </Card>
          </motion.div>
        )}
      </Section>
    </PageContainer>
  );
}
